import React, { FC } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GridRowId } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';

import { ITimesheetCellDTO } from 'src/api/dtos';
import { AttachmentIcon, ChatSquareIcon } from 'src/shared/components';
import { Tooltip } from 'src/shared/components';
import { DataGridDragCellWrapper, DataGridDropCellWrapper } from 'src/shared/components/data-grid';
import { PermissionsEnums } from 'src/shared/enums';
import { usePermissions } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { formatInputDuration } from 'src/shared/utils';
import { getDurationFormat } from 'src/store/user/user.selectors';

import { TimesheetCellContextMenu } from './TimesheetCellContextMenu';

interface Props {
  id: GridRowId;
  field: string;
  value: ITimesheetCellDTO;
  isEdited: boolean;
  disabled: boolean;
  isOutsidePayPeriod: boolean;
  isAbsenceIgnored: boolean;
  onAddExpense: () => void;
  onAddNode: () => void;
}

export const TimesheetDateViewCell: FC<Props> = ({
  id,
  field,
  value,
  isEdited,
  disabled,
  isOutsidePayPeriod,
  isAbsenceIgnored,
  onAddExpense,
  onAddNode,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const durationFormat = useSelector(getDurationFormat);

  const { duration, expenses, note } = value;
  const hasInputExpense = usePermissions(PermissionsEnums.WTS_INPUT_EXPENSE);

  const getTooltip = () => {
    if (isOutsidePayPeriod) {
      return t('timesheets.toAddHoursToThisDateSelectTheRightPay');
    }

    if (isAbsenceIgnored) {
      return t('timesheets.scheduledAbsenceIsIgnoredOnTheStatDay');
    }

    return undefined;
  };

  const hasAttachment = expenses.some((expense) => !!expense.attachment?.referenceId);

  return (
    <DataGridDropCellWrapper
      id={id}
      field={field}
      disabled={disabled}
      droppingSx={{
        background: (theme) => theme.palette.secondary.main,
        border: (theme) => `1px dotted ${theme.palette.primary.main}`,
      }}
      activeSx={{ border: (theme) => `1px dotted ${theme.palette.primary.main}` }}
    >
      <DataGridDragCellWrapper
        id={id}
        field={field}
        disabled={!duration}
        sx={{
          outline: 'none',
          '&:hover': {
            border: duration ? (theme) => `1px solid ${theme.palette.primary.main}` : 'initial',
          },
        }}
        draggingSx={{
          border: (theme) => `1px solid ${theme.palette.primary.main}`,
          background: (theme) => theme.palette.background.default,
        }}
      >
        <Tooltip title={!!getTooltip() && <Typography fontSize="0.875rem">{getTooltip()}</Typography>} showIcon={false}>
          <Stack
            direction="row"
            width="100%"
            height="100%"
            justifyContent="space-between"
            alignItems="center"
            position="relative"
            sx={{
              background: (theme) =>
                isEdited
                  ? `${theme.palette.mode === 'dark' ? theme.palette.secondary.dark : theme.palette.secondary.main}`
                  : 'initial',
            }}
          >
            <Stack justifyContent="center" alignItems="center" flex={1}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography fontSize="0.875rem">
                  {formatInputDuration(duration, durationFormat, 'seconds', false)}
                </Typography>{' '}
                {note && (
                  <Tooltip title={<Typography fontSize="0.875rem">{note}</Typography>} showIcon={false}>
                    <Stack lineHeight={0}>
                      <ChatSquareIcon size={14} color={theme.palette.primary.main} />
                    </Stack>
                  </Tooltip>
                )}
              </Stack>
              {!!expenses.length && (
                <Tooltip
                  title={expenses.map((expense) => (
                    <Typography key={expense.id} fontSize="0.875rem">
                      {expense.description}: {expense?.memoAmount?.toFixed(2)}
                    </Typography>
                  ))}
                  showIcon={false}
                >
                  <Typography fontSize="0.875rem">
                    ({expenses[0]?.memoAmount?.toFixed(2)}){' '}
                    {expenses.length > 1 && (
                      <Typography component="span" fontSize="0.875rem" color={(theme) => theme.palette.primary.main}>
                        +{expenses.length - 1}
                      </Typography>
                    )}
                  </Typography>
                </Tooltip>
              )}
            </Stack>

            {hasAttachment && (
              <Box sx={{ mr: 4 }}>
                <AttachmentIcon height={15} width={8} color={theme.palette.primary.main} />
              </Box>
            )}

            <TimesheetCellContextMenu
              cell={value}
              disabled={disabled}
              hasInputExpense={hasInputExpense}
              onAddExpense={onAddExpense}
              onAddNote={onAddNode}
            />
          </Stack>
        </Tooltip>
      </DataGridDragCellWrapper>
    </DataGridDropCellWrapper>
  );
};
